import { useId } from "react";

import { ColocaChoiceCards } from "@/shared/components/forms/ColocaChoiceCards";
import useTranslations from "@/shared/lang/useTranslations";

import { useCryptoSwapLabels } from "../../hooks/useCryptoSwapLabels";
import { NetworkIcon } from "./NetworkIcon";

export interface NetworkPickerProps {
  networks: string[];
  value: string;
  onChange: (network: string) => void;
  title: string;
  hint: string;
  disabled?: boolean;
}

/** The chain the USDT travels on: it must match the wallet's, and the wallet pays its fee in this asset. */
export function NetworkPicker({ networks, value, onChange, title, hint, disabled }: NetworkPickerProps) {
  const { t } = useTranslations("cryptoSwaps");
  const labels = useCryptoSwapLabels();
  const titleId = useId();

  return (
    <section aria-labelledby={titleId} className="flex flex-col gap-3">
      <div className="flex flex-col gap-1">
        <h2 id={titleId} className="text-lg font-semibold tracking-tight text-foreground">
          {title}
        </h2>
        <p className="text-sm leading-relaxed text-muted-foreground">{hint}</p>
      </div>
      <ColocaChoiceCards
        aria-labelledby={titleId}
        value={value}
        onValueChange={onChange}
        disabled={disabled}
        columns={networks.length > 1 ? 2 : 1}
        items={networks.map((code) => {
          const rule = labels.networkRule(code);
          return {
            value: code,
            icon: <NetworkIcon network={code} size={28} />,
            label: labels.networkShort(code),
            description: rule
              ? t("sell_network_option", { standard: rule.standard, gas: rule.gasAsset })
              : undefined,
          };
        })}
      />
    </section>
  );
}
